/**
 * Home — the landing screen for every signed-in player (specs/home-page). Container: header, data
 * hooks, navigation; `HomeScreenView` draws the regions.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import { useRouter, useFocusEffect, type Href } from 'expo-router';
import { useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../layout/PageHeader';
import { HEADER_FLAVORS } from '../../layout/constants/pageHeader.constants';
import { useTheme } from '../../../src/theme/useTheme';
import { useAnalytics } from '../../../src/analytics/useAnalytics';
import { ANALYTICS_EVENTS } from '../../../src/analytics/events';
import { useSupabaseAuth } from '../../../src/shared/contexts/SupabaseAuthContext';
import { useGuestGuard } from '../../../src/shared/hooks/useGuestGuard';
import { GUEST_AUTH_ACTIONS } from '../../../src/shared/hooks/useGuestGuard.constants';
import { useDailyClaimAction } from '../../../src/economy/hooks/useDailyClaimAction';
import { DailyRewardModal } from '../../../src/economy/components/DailyRewardModal';
import { challengesQueryKeys } from '../../../src/challenges/constants/challengesQueryKeys';
import { useTodaySlateQuery } from '../../../src/home/hooks/useTodaySlateQuery';
import { useWeeklyQuestsQuery } from '../../../src/home/hooks/useWeeklyQuestsQuery';
import { useChestCelebration } from '../../../src/home/hooks/useChestCelebration';
import { useRefetchAt } from '../../../src/home/hooks/useRefetchAt';
import { useHomeChallengeTiles } from '../../../src/home/hooks/useHomeChallengeTiles';
import { useHomeLastResults } from '../../../src/home/hooks/useHomeLastResults';
import { useHomeStreak } from '../../../src/home/hooks/useHomeStreak';
import { useAcceptInvite } from '../../../src/home/hooks/useAcceptInvite';
import { usePendingSlatePrefillStore, type SlatePrefillOpponent } from '../../../src/home/stores/pendingSlatePrefillStore';
import {
  visibleSlateMatches,
  slateToPickSlips,
  slatePrefillDateIso,
} from '../../../src/home/helpers/slateToPickSlips.helper';
import { homeQueryKeys } from '../../../src/home/constants/homeQueryKeys';
import { HOME_CHEST_LOCK_KEY, HOME_COPY, HOME_ROUTES, HOME_TEST_IDS } from '../../../src/home/constants/home.constants';
import { TodayChallengeCard } from '../TodayChallengeCard/TodayChallengeCard';
import { StreakCard } from '../StreakCard/StreakCard';
import { WeeklyQuestCard } from '../WeeklyQuestCard/WeeklyQuestCard';
import { ChestCelebrationModal } from '../ChestCelebrationModal/ChestCelebrationModal';
import { HomeScreenView } from './HomeScreenView';
import { getHomeScreenStyles } from './HomeScreen.styles';

type SlateSendEvent = typeof ANALYTICS_EVENTS.HOME_SLATE_SEND_TAPPED | typeof ANALYTICS_EVENTS.HOME_SLATE_MONKEY_TAPPED;

export function HomeScreen() {
  const theme = useTheme();
  const styles = useMemo(() => getHomeScreenStyles(theme), [theme]);
  const router = useRouter();
  const queryClient = useQueryClient();
  const { track } = useAnalytics();
  const { userId } = useSupabaseAuth();
  const { requireAuth } = useGuestGuard();

  const slateQuery = useTodaySlateQuery();
  const questsQuery = useWeeklyQuestsQuery(userId ?? null);
  const tiles = useHomeChallengeTiles();
  const lastResults = useHomeLastResults();
  const streak = useHomeStreak();
  const collectDaily = useDailyClaimAction();
  const { accept, acceptingId } = useAcceptInvite();
  const slate = slateQuery.data ?? null;
  const quests = questsQuery.data ?? null;
  const chest = useChestCelebration({ quests, userId: userId ?? null });

  useEffect(() => {
    track(ANALYTICS_EVENTS.HOME_VIEWED);
  }, [track]);

  // Coming back to Home refreshes only what has gone stale, and only what Home reads (the Arena list feeds the
  // Active / Invite rows, the finished preview feeds Last results): no duplicate fetch on first mount, and no
  // refetch of the group / active / history queries Home never shows.
  useFocusEffect(
    useCallback(() => {
      void queryClient.refetchQueries({ queryKey: homeQueryKeys.slateToday(), type: 'active', stale: true });
      void queryClient.refetchQueries({ queryKey: homeQueryKeys.quests(userId ?? null), type: 'active', stale: true });
      void queryClient.refetchQueries({ queryKey: challengesQueryKeys.list(userId ?? null), type: 'active', stale: true });
      void queryClient.refetchQueries({ queryKey: challengesQueryKeys.historyPreview(userId ?? null), type: 'active', stale: true });
    }, [queryClient, userId]),
  );

  // The slate rolls over at UTC midnight and the quests at Monday 00:00 UTC: fetch the new ones as they land.
  useRefetchAt(slate?.nextSlateAt, homeQueryKeys.slateToday());
  useRefetchAt(quests?.endsAt, homeQueryKeys.quests(userId ?? null));

  const navigate = useCallback((href: string) => router.push(href as Href), [router]);

  const handlePressChallenge = useCallback(
    (challengeId: string) => navigate(HOME_ROUTES.arenaChallenge(challengeId)),
    [navigate],
  );
  const handleChallengeFriend = useCallback(() => navigate(HOME_ROUTES.CREATE), [navigate]);
  const handleFindRival = useCallback(() => navigate(HOME_ROUTES.ARENA_OPEN_BOARD), [navigate]);
  const handleSeeAllMatches = useCallback(() => navigate(HOME_ROUTES.FIXTURES), [navigate]);
  const handleAccept = useCallback(
    (challengeId: string, stakeCoins: number) => {
      void accept(challengeId, stakeCoins);
    },
    [accept],
  );
  // A guest tapped a task they cannot do, or the locked chest: the shared guest prompt offers sign-up (AC-24).
  const handleLockedTask = useCallback((taskKey: string) => {
    requireAuth(taskKey === HOME_CHEST_LOCK_KEY ? HOME_COPY.GUEST_CHEST_ACTION : HOME_COPY.GUEST_LOCK_ACTION);
  }, [requireAuth]);
  // The streak card opens the shared Daily reward sheet (the menu coin chip's); its Collect is the Profile
  // card's Collect: one shared action, one source of truth, and the coins are seen flying to the header (AC-34).
  const [isDailyRewardOpen, setIsDailyRewardOpen] = useState(false);
  const handleOpenDailyReward = useCallback(() => {
    if (!requireAuth(GUEST_AUTH_ACTIONS.DAILY_REWARD)) return;
    track(ANALYTICS_EVENTS.HOME_STREAK_TAPPED, { can_claim: streak.model.canClaim });
    setIsDailyRewardOpen(true);
  }, [requireAuth, track, streak.model.canClaim]);
  const handleCloseDailyReward = useCallback(() => setIsDailyRewardOpen(false), []);
  const handleCollectStreak = useCallback(() => {
    void collectDaily();
  }, [collectDaily]);

  const refetchSlate = useCallback(() => {
    void slateQuery.refetch();
  }, [slateQuery]);
  const refetchQuests = useCallback(() => {
    void questsQuery.refetch();
  }, [questsQuery]);

  // "Send to a Friend" (AC-10) and "Beat the Monkey" (AC-10b): stash the still-upcoming slate as pick
  // slips, then open Create; the provider consumes the hand-off (and, for the Monkey, picks the rival).
  const sendSlate = useCallback(
    (opponent: SlatePrefillOpponent | undefined, event: SlateSendEvent) => {
      const matches = visibleSlateMatches(slate, Date.now());
      const slips = slateToPickSlips(matches);
      if (!slate || slips.length === 0) return;
      track(event, { games_count: matches.length, picks_count: slips.length });
      usePendingSlatePrefillStore.getState().setPrefill({
        slips,
        selectedDateIso: slatePrefillDateIso(matches),
        slateDate: slate.slateDate,
        opponent,
      });
      navigate(HOME_ROUTES.CREATE);
    },
    [slate, navigate, track],
  );
  const handleSendSlate = useCallback(() => sendSlate(undefined, ANALYTICS_EVENTS.HOME_SLATE_SEND_TAPPED), [sendSlate]);
  const handleBeatMonkey = useCallback(() => sendSlate('monkey', ANALYTICS_EVENTS.HOME_SLATE_MONKEY_TAPPED), [sendSlate]);

  return (
    <View style={styles.screen} testID={HOME_TEST_IDS.screen}>
      <PageHeader flavor={HEADER_FLAVORS.CHALLENGES} props={{}} />
      <View style={styles.body}>
        <HomeScreenView
          slateSlot={
            <TodayChallengeCard
              slate={slate}
              isLoading={slateQuery.isPending}
              isError={slateQuery.isError}
              onRetry={refetchSlate}
              onSend={handleSendSlate}
              onBeatMonkey={handleBeatMonkey}
              onSeeAll={handleSeeAllMatches}
            />
          }
          streakSlot={<StreakCard model={streak.model} onPress={handleOpenDailyReward} />}
          weeklySlot={
            <WeeklyQuestCard
              quests={quests}
              isLoading={questsQuery.isPending}
              isError={questsQuery.isError}
              onRetry={refetchQuests}
              onLockedTaskPress={handleLockedTask}
            />
          }
          tiles={{
            active: tiles.active,
            invite: tiles.invite,
            isLoading: tiles.isLoading,
            isError: tiles.isError,
            onRetry: tiles.refetch,
            onPressActive: handlePressChallenge,
            onPressInvite: handlePressChallenge,
            onAcceptInvite: handleAccept,
            acceptingId,
          }}
          results={{
            results: lastResults.results,
            isLoading: lastResults.isLoading,
            isError: lastResults.isError,
            onRetry: lastResults.refetch,
            onPressResult: handlePressChallenge,
            onChallengeFriend: handleChallengeFriend,
            onFindRival: handleFindRival,
          }}
        />
      </View>
      <ChestCelebrationModal visible={chest.isOpen} rewardCoins={chest.rewardCoins} onDismiss={chest.dismiss} />
      <DailyRewardModal
        isVisible={isDailyRewardOpen}
        onDismiss={handleCloseDailyReward}
        onClaim={handleCollectStreak}
        testID={HOME_TEST_IDS.streakModal}
      />
    </View>
  );
}
